/* CheerCove: read-only cart progress. No cart, discount, or shipping mutations. */
if (!customElements.get('cheercove-cart-progress')) {
  class CheerCoveCartProgress extends HTMLElement {
    static get observedAttributes() { return ['data-cart', 'hidden']; }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue || (name === 'hidden' && newValue === null)) return;
      if (this.snapshotQueued) return;
      this.snapshotQueued = true;
      queueMicrotask(() => {
        this.snapshotQueued = false;
        if (this.isConnected) this.applyServerSnapshot();
      });
    }

    applyServerSnapshot() {
      if (!this.dataset.cart) return false;
      try {
        const cart = JSON.parse(this.dataset.cart);
        if (!Array.isArray(cart.items) || !cart.currency) return false;
        clearTimeout(this.timer);
        this.controller?.abort();
        this.paint(cart);
        return true;
      } catch (error) {
        console.warn('CheerCove progress: could not read section cart data.', error);
        return false;
      }
    }

    connectedCallback() {
      this.onUpdate = () => this.schedule();
      this.onVisibility = () => { if (!document.hidden) this.schedule(); };
      document.addEventListener('cart:update', this.onUpdate);
      document.addEventListener('discount:update', this.onUpdate);
      document.addEventListener('visibilitychange', this.onVisibility);
      if (!this.applyServerSnapshot()) this.schedule();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:update', this.onUpdate);
      document.removeEventListener('discount:update', this.onUpdate);
      document.removeEventListener('visibilitychange', this.onVisibility);
      clearTimeout(this.timer);
      this.controller?.abort();
    }

    schedule() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.timer = setTimeout(() => this.refresh(), 120);
    }

    async refresh() {
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
        const cart = await response.json();
        if (!this.isConnected || controller.signal.aborted) return;
        this.paint(cart);
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Keep the last valid display on a temporary network failure.
        console.warn('CheerCove progress could not refresh:', error);
      }
    }

    paint(cart) {
      if (!Array.isArray(cart.items) || !cart.currency) throw new Error('Incomplete cart response');
      const raw = this.dataset[cart.currency.toLowerCase()];
      if (!raw || cart.items.length === 0) { this.hidden = true; return; }
      const values = raw.split(',').map(Number);
      const [shipping, tier1, save1, tier2, save2] = values.map(value => Math.round(value * 100));
      if (values.length !== 5 || !values.every(Number.isFinite) || !(shipping > 0 && tier1 > shipping && tier2 > tier1 && save1 > 0 && save2 > 0)) {
        this.hidden = true;
        console.warn('CheerCove progress: thresholds must satisfy 0 < shipping < tier 1 < tier 2.');
        return;
      }
      let amount = 0;
      let shippingAmount = 0;
      for (const item of cart.items) {
        if (!Number.isFinite(item.original_line_price)) throw new Error('Missing original line amount');
        shippingAmount += item.original_line_price;
        if (String(item.product_id) !== this.dataset.excluded) {
          amount += item.original_line_price;
        }
      }
      // Bundle value counts toward shipping, but not spend-to-save thresholds.
      const shippingReached = shippingAmount >= shipping;
      const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: cart.currency
      });
      const money = cents => formatter.format(cents / 100);
      const rewardFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: cart.currency,
        minimumFractionDigits: 0, maximumFractionDigits: 2
      });
      const rewardMoney = cents => rewardFormatter.format(cents / 100);
      const interpolate = (text, threshold, savings, qualifyingAmount = amount) => (text || '')
        .split('{remaining}').join(money(Math.max(0, threshold - qualifyingAmount)))
        .split('{discount}').join(rewardMoney(savings))
        .split('{threshold}').join(rewardMoney(threshold));
      const goals = [shipping, tier1, tier2];
      const savings = [0, save1, save2];
      const reachedGoals = [shippingReached, amount >= tier1, amount >= tier2];
      const next = reachedGoals.findIndex(reached => !reached);
      const message = next < 0
        ? interpolate(this.dataset.completeMessage, tier2, save2)
        : interpolate(next === 0 ? this.dataset.shippingMessage : this.dataset.discountMessage, goals[next], savings[next], next === 0 ? shippingAmount : amount);
      const messageNode = this.querySelector('.ccp-message');
      if (messageNode.textContent !== message) messageNode.textContent = message;
      // Icons are centered over three equal-width label columns.
      // Shipping and discount eligibility use independent qualifying amounts.
      let percentage = 100;
      if (next === 0) {
        percentage = (shippingAmount / shipping) * (100 / 6);
      } else if (next === 1) {
        percentage = 100 / 6 + (amount / tier1) * (100 / 3);
      } else if (next === 2) {
        percentage = 50 + ((amount - tier1) / (tier2 - tier1)) * (100 / 3);
      }
      percentage = Math.max(0, Math.min(100, percentage));
      this.querySelector('.ccp-fill').style.width = `${percentage}%`;
      const track = this.querySelector('.ccp-track');
      track.setAttribute('aria-valuenow', String(Math.round(percentage)));
      track.setAttribute('aria-valuetext', message);
      const labels = [this.dataset.shippingLabel, this.dataset.tier1Label, this.dataset.tier2Label];
      this.querySelectorAll('.ccp-milestone').forEach((node, index) => {
        node.querySelector('.ccp-label').textContent = interpolate(labels[index], goals[index], savings[index]);
        node.dataset.reached = String(reachedGoals[index]);
      });
      const achievedTexts = [this.dataset.shippingReached, this.dataset.tier1Reached, this.dataset.tier2Reached];
      let highestReached = -1;
      reachedGoals.forEach((reached, index) => {
        if (reached) highestReached = index;
      });
      let shown = 0;
      this.querySelectorAll('.ccp-achievement').forEach((node, index) => {
        // Show only the highest reached tier's message, never lower-tier messages.
        const reached = index === highestReached;
        const text = interpolate(achievedTexts[index], goals[index], savings[index]);
        const show = reached && text.trim().length > 0;
        if (node.textContent !== text) node.textContent = text;
        node.hidden = !show;
        if (show) shown += 1;
      });
      this.querySelector('.ccp-achievements').hidden = shown === 0;
      this.hidden = false;
    }
  }
  customElements.define('cheercove-cart-progress', CheerCoveCartProgress);
}